import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { motion, AnimatePresence } from "framer-motion";

import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";

const buttonVariants = cva("inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium cursor-pointer disabled:pointer-events-none disabled:opacity-50 transition-colors duration-200 ease-in-out [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive", {
	variants: {
		variant: {
			default: "bg-primary text-primary-foreground shadow-xs hover:bg-primary/90",
			destructive: "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
			outline: "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
			secondary: "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
			ghost: "hover:bg-accent/10 hover:text-accent-foreground dark:hover:bg-accent/20 dark:hover:text-accent-foreground",
			link: "text-primary underline-offset-4 hover:underline",
		},
		size: {
			default: "h-9 px-4 py-2 has-[>svg]:px-3",
			sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
			lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
			icon: "size-9",
		},
	},
	defaultVariants: {
		variant: "default",
		size: "default",
	},
});

function Button({
	className,
	variant,
	size,
	asChild = false,
	...props
}: React.ComponentProps<"button"> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean;
	}) {
	const Comp = asChild ? Slot : "button";

	return <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

interface IconButtonProps extends React.ComponentProps<"button">, VariantProps<typeof buttonVariants> {
	asChild?: boolean;
	icon: LucideIcon;
	text?: string;
}

/**
 * IconButton renders a button with an icon and optional text.
 */
function IconButton({ icon: Icon, text, className, variant, size, iconPosition = "left", ...props }: IconButtonProps & { iconPosition?: "left" | "right" }) {
	const btnSize = text ? "default" : size;
	return (
		<Button variant={variant} size={btnSize} className={cn(className)} {...props}>
			{iconPosition === "left" && <Icon className="w-4 h-4" />}
			{text}
			{iconPosition === "right" && <Icon className="w-4 h-4" />}
		</Button>
	);
}

interface AnimatedIconButtonProps extends Omit<React.ComponentProps<"button">, "children" | "onDrag" | "onDragStart" | "onDragEnd" | "onAnimationStart" | "onAnimationEnd" | "onAnimationIteration">, VariantProps<typeof buttonVariants> {
	icon: LucideIcon;
	text: string;
	expandDirection?: "left" | "right";
}

/**
 * AnimatedIconButton renders a button with an icon that smoothly expands to show text on hover.
 */
function AnimatedIconButton({ icon: Icon, text, className, variant = "default", size = "default", expandDirection = "right", ...props }: AnimatedIconButtonProps) {
	const [isHovered, setIsHovered] = React.useState(false);

	// Get height class based on size
	const getHeightClass = () => {
		switch (size) {
			case "sm":
				return "h-8";
			case "lg":
				return "h-10";
			default:
				return "h-9";
		}
	};

	// Get padding values based on size
	const getPadding = (expanded: boolean) => {
		if (!expanded) return "0.5rem";

		switch (size) {
			case "sm":
				return "0.75rem";
			case "lg":
				return "1.5rem";
			default:
				return "1rem";
		}
	};

	// Get minimum width based on size
	const getMinWidth = () => {
		switch (size) {
			case "sm":
				return "2rem";
			case "lg":
				return "2.5rem";
			default:
				return "2.25rem";
		}
	};

	return (
		<motion.button
			className={cn(
				// Base styles without size classes to avoid conflicts
				"inline-flex items-center justify-center rounded-md text-sm font-medium cursor-pointer disabled:pointer-events-none disabled:opacity-50 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive overflow-hidden relative",
				// Only apply variant styles
				buttonVariants({ variant, size: undefined })
					.split(" ")
					.filter((cls) => !cls.startsWith("h-") && !cls.startsWith("px-") && !cls.startsWith("py-") && !cls.startsWith("size-") && !cls.startsWith("gap-"))
					.join(" "),
				// Apply height manually
				getHeightClass(),
				className,
			)}
			onHoverStart={() => setIsHovered(true)}
			onHoverEnd={() => setIsHovered(false)}
			animate={{
				width: "auto",
				paddingLeft: getPadding(isHovered),
				paddingRight: getPadding(isHovered),
			}}
			transition={{
				type: "spring",
				stiffness: 300,
				damping: 25,
				mass: 0.8,
			}}
			style={{
				minWidth: getMinWidth(),
			}}
			{...props}>
			<div className="flex items-center justify-center">
				<Icon className="w-4 h-4 shrink-0" />
				<AnimatePresence>
					{isHovered && (
						<motion.span
							initial={{
								opacity: 0,
								width: 0,
								marginLeft: 0,
							}}
							animate={{
								opacity: 1,
								width: "auto",
								marginLeft: "0.5rem",
							}}
							exit={{
								opacity: 0,
								width: 0,
								marginLeft: 0,
							}}
							transition={{
								type: "spring",
								stiffness: 300,
								damping: 25,
								mass: 0.8,
							}}
							className="whitespace-nowrap text-sm font-medium overflow-hidden">
							{text}
						</motion.span>
					)}
				</AnimatePresence>
			</div>
		</motion.button>
	);
}

export { Button, IconButton, AnimatedIconButton, buttonVariants };
